import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useBedrijven } from '../../api/klanten.ts';
import { zonderLeeg } from '../../api/mutaties.ts';
import type { Bedrijfsrelatie } from '../../api/typen.ts';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { Lijstpaginering } from '../../componenten/Paginering.tsx';
import { StatusChip } from '../../componenten/StatusChip.tsx';
import { Zoekveld } from '../../componenten/Zoekveld.tsx';
import { ofStreepje } from '../../helpers/formatteer.ts';
import { bedrijfsrelatieLabels, opties } from '../../helpers/labels.ts';
import { useZoekparameters } from '../../hooks/useZoekparameters.ts';
import { BedrijfFormulier } from './BedrijfFormulier.tsx';

const relatieOpties = opties(bedrijfsrelatieLabels);

/**
 * Het overzicht van bedrijven met zoeken en een filter op relatie.
 * @returns De pagina.
 */
export function BedrijvenPagina() {
  const { zoek, offset, limiet, krijg, zet, zetOffset } = useZoekparameters();
  const relatie = krijg('relatie') as Bedrijfsrelatie | '';
  const bedrijven = useBedrijven(zonderLeeg({ zoek, relatie: relatie || undefined, limiet, offset }));
  const [nieuw, setNieuw] = useState(false);
  const navigeer = useNavigate();

  return (
    <Pagina
      titel="Bedrijven"
      acties={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNieuw(true)}>
          Nieuw bedrijf
        </Button>
      }
    >
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Zoekveld waarde={zoek} onChange={(waarde) => zet({ zoek: waarde })} placeholder="Zoek op naam" />
          <TextField
            select
            size="small"
            label="Relatie"
            value={relatie}
            onChange={(gebeurtenis) => zet({ relatie: gebeurtenis.target.value })}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Alle relaties</MenuItem>
            {relatieOpties.map((optie) => (
              <MenuItem key={optie.waarde} value={optie.waarde}>
                {optie.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Laadstatus query={bedrijven} leeg="Geen bedrijven gevonden.">
          {(lijst) => (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Naam</TableCell>
                    <TableCell>Relatie</TableCell>
                    <TableCell>Sector</TableCell>
                    <TableCell>Telefoon</TableCell>
                    <TableCell>Website</TableCell>
                    <TableCell>KvK</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lijst.map((bedrijf) => (
                    <TableRow key={bedrijf.id} hover>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/bedrijven/${bedrijf.id}`}
                          underline="hover"
                          sx={{ fontWeight: 500 }}
                        >
                          {bedrijf.naam}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <StatusChip soort="relatie" waarde={bedrijf.relatie} />
                      </TableCell>
                      <TableCell>{ofStreepje(bedrijf.sector)}</TableCell>
                      <TableCell>{ofStreepje(bedrijf.telefoon)}</TableCell>
                      <TableCell>
                        {bedrijf.website ? (
                          <Link href={bedrijf.website} target="_blank" rel="noreferrer" underline="hover">
                            {bedrijf.website.replace(/^https?:\/\//, '')}
                          </Link>
                        ) : (
                          '–'
                        )}
                      </TableCell>
                      <TableCell>{ofStreepje(bedrijf.kvkNummer)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Laadstatus>
        <Lijstpaginering offset={offset} limiet={limiet} aantal={bedrijven.data?.length ?? 0} onOffset={zetOffset} />
      </Paper>
      <BedrijfFormulier
        open={nieuw}
        onSluit={() => setNieuw(false)}
        onOpgeslagen={(bedrijf) => void navigeer(`/bedrijven/${bedrijf.id}`)}
      />
    </Pagina>
  );
}
